import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import "@/App.css";
import parknovaLogo from "@/assets/brand/parknova-logo.png";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Sucursales from "@/pages/Sucursales";
import Empresas from "@/pages/Empresas";
import Usuarios from "@/pages/Usuarios";
import Cortes from "@/pages/Cortes";
import AuditoriaFactorRiesgo from "@/pages/AuditoriaFactorRiesgo";
import PernoctasAdmin from "@/pages/PernoctasAdmin";
import OperativoHome from "@/pages/OperativoHome";
import Terminal from "@/pages/Terminal";
import Valet from "@/pages/Valet";
import SeleccionSucursal from "@/pages/SeleccionSucursal";
import ConfiguracionFinanciera from "@/pages/ConfiguracionFinanciera";
import EnConstruccion from "@/pages/EnConstruccion";
import Servicios from "@/pages/Servicios";
import Productos from "@/pages/Productos";
import Tarifas from "@/pages/Tarifas";
import PensionesPostpago from "@/pages/PensionesPostpago";
import Bitacora from "@/pages/Bitacora";
import Paquetes from "@/pages/Paquetes";
import Recargas from "@/pages/Recargas";
import ValidacionRecargas from "@/pages/ValidacionRecargas";
import ConfiguracionSistema from "@/pages/ConfiguracionSistema";
import LandingPageCliente from "@/pages/LandingPageCliente";
import DashboardResumen from "@/pages/DashboardResumen";

const OP_ROLES = ["Cajero", "Valet"];

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <Loader2 className="h-8 w-8 animate-spin text-[#FFCC00]" />
    </div>
  );
}

function Landing() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Login />;
  if (user.rol === "TenantAdmin") return <Navigate to="/app/dashboard" replace />;
  return <Navigate to="/app" replace />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/" replace />;
  // Operational roles never reach the admin console
  if (OP_ROLES.includes(user.rol)) return <Navigate to="/operativo" replace />;
  return children;
}

// Guard por rol: si el rol no está permitido, redirige al home del admin.
function RoleRoute({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/" replace />;
  if (OP_ROLES.includes(user.rol)) return <Navigate to="/operativo" replace />;
  if (!roles.includes(user.rol)) return <Navigate to="/app" replace />;
  return children;
}

// El SuperAdmin no gestiona sucursales; su home es Empresas. El TenantAdmin ve el listado de sus sucursales.
function HomeRoute() {
  const { user } = useAuth();
  if (user?.rol === "SuperAdmin") return <Navigate to="/app/empresas" replace />;
  return <Sucursales />;
}

function OperativoRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/" replace />;
  if (!OP_ROLES.includes(user.rol)) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/t/:folio" element={<LandingPageCliente />} />
          <Route path="/e/:folio" element={<LandingPageCliente />} />
          <Route path="/app" element={<Protected><HomeRoute /></Protected>} />
          <Route path="/app/dashboard" element={<RoleRoute roles={["TenantAdmin"]}><DashboardResumen /></RoleRoute>} />
          <Route path="/app/empresas" element={<Protected><Empresas /></Protected>} />
          <Route path="/app/usuarios" element={<Protected><Usuarios /></Protected>} />
          <Route path="/app/cortes" element={<RoleRoute roles={["TenantAdmin"]}><Cortes /></RoleRoute>} />
          <Route path="/app/auditoria-factor" element={<RoleRoute roles={["TenantAdmin"]}><AuditoriaFactorRiesgo /></RoleRoute>} />
          <Route path="/app/pernoctas" element={<RoleRoute roles={["TenantAdmin"]}><PernoctasAdmin /></RoleRoute>} />
          <Route path="/app/bitacora" element={<Protected><Bitacora /></Protected>} />
          <Route path="/app/paquetes" element={<RoleRoute roles={["SuperAdmin"]}><Paquetes /></RoleRoute>} />
          <Route path="/app/recargas" element={<RoleRoute roles={["TenantAdmin"]}><Recargas /></RoleRoute>} />
          <Route path="/app/validacion-recargas" element={<RoleRoute roles={["SuperAdmin"]}><ValidacionRecargas /></RoleRoute>} />
          <Route path="/app/configuracion-sistema" element={<RoleRoute roles={["SuperAdmin"]}><ConfiguracionSistema /></RoleRoute>} />
          <Route path="/app/configuracion-financiera" element={<Protected><ConfiguracionFinanciera /></Protected>} />
          <Route path="/app/tarifas" element={<Protected><Tarifas /></Protected>} />
          <Route path="/app/pensiones" element={<Protected><PensionesPostpago /></Protected>} />
          <Route path="/app/servicios" element={<Protected><Servicios /></Protected>} />
          <Route path="/app/productos" element={<Protected><Productos /></Protected>} />
          <Route path="/operativo" element={<OperativoRoute><Terminal /></OperativoRoute>} />
          <Route path="/operativo/valet" element={<OperativoRoute><Valet /></OperativoRoute>} />
          <Route path="/operativo/seleccion" element={<OperativoRoute><SeleccionSucursal /></OperativoRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
